import { useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';

import { HostPlayP2pClientEventType } from '@genially/p2p-client';

import type { ActiveReaction } from '../types/reactions';

const REACTION_LIFETIME_MS = 3500;
const REACTION_EVENT = HostPlayP2pClientEventType.PlayerReactionReceived;

export function useReactions(
  client: {
    on: (event: any, listener: any) => void;
    off: (event: any, listener: any) => void;
  },
  maxReactions: number,
): ActiveReaction[] {
  const [reactions, setReactions] = useState<ActiveReaction[]>([]);
  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const handleReaction = ({
      player,
      reactionType,
    }: Pick<ActiveReaction, 'player' | 'reactionType'>) => {
      const id = nanoid();

      const timeoutId = setTimeout(() => {
        setReactions(prev => prev.filter(r => r.id !== id));
        timeoutRefs.current = timeoutRefs.current.filter(t => t !== timeoutId);
      }, REACTION_LIFETIME_MS);

      timeoutRefs.current.push(timeoutId);

      setReactions(prev => {
        if (prev.length >= maxReactions) return prev;
        return [...prev, { id, player, reactionType }];
      });
    };

    client.on(REACTION_EVENT as any, handleReaction);

    return () => {
      client.off(REACTION_EVENT as any, handleReaction);
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, [client, maxReactions]);

  return reactions;
}
